<template>
  <div 
    class="addTimeSheet-container addTimeSheet-page--glass" 
    :class="{ 'resizing': isResizing, 'has-custom-size': hasCustomSize, 'addTimeSheet-container--daily': dailyRows.length }"
    :style="{ width: modalWidth + 'px', height: modalHeight + 'px' }"
  >
    <div class="addTimeSheet-form-section">
      <div class="addTimeSheet-form-header">
        <div class="addTimeSheet-header-content">
          <div>
            <h2>{{ isEditMode ? 'Edit Time Sheet' : 'Add A New Time Sheet' }}</h2>
            <p class="addTimeSheet-form-description">
              {{ isEditMode ? 'Update dates and day-wise hours for this timesheet.' : 'Fill in the details below to create a new time sheet entry.' }}
            </p>
          </div>
          <button 
            type="button" 
            class="addTimeSheet-close-icon-btn" 
            @click="goBack"
            title="Close"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      <!-- Unified Form -->
      <form class="addTimeSheet-form" @submit.prevent="submitTimeSheet">
        <div class="addTimeSheet-form-grid">
          <!-- Consultant Name and Upload Document Toggle in one row -->
          <div class="addTimeSheet-form-group addTimeSheet-form-group-full addTimeSheet-consultant-upload-row">
            <div class="addTimeSheet-consultant-name-field">
              <label for="consultantName">Consultant Name <span class="required-asterisk">*</span></label>
              <!-- When prefilled: show read-only input so consultant name is always visible (like CreateBill) -->
              <input
                v-if="isConsultantPrefilled"
                id="consultantName"
                type="text"
                :value="displayConsultantName"
                class="addTimeSheet-input addTimeSheet-input-readonly"
                readonly
              />
              <LabelDropdown
                v-else
                label="Consultant Name"
                hide-label
                select-id="consultantName"
                v-model="consultantSelectValue"
                searchable
                required
                flex
                custom-class="addTimeSheet-consultant-dropdown"
                menu-class="invoice-consultant-type-dropdown-menu"
                :options="consultantDropdownOptions"
                @change="handleConsultantChange"
              />
              <div v-if="isConsultantPrefilled" class="addTimeSheet-helper-text">
                Consultant is pre-selected and cannot be changed
              </div>
            </div>
            <div class="addTimeSheet-upload-toggle-field">
              <div class="addTimeSheet-toggle-wrapper">
                <label class="addTimeSheet-toggle-label">
                  <span class="addTimeSheet-toggle-text">
                    <i class="bi bi-cloud-upload"></i> Upload Document for Data Extraction
                    <LabelWithHelp
                      help-only
                      help-text="Enable to upload a PDF or image file and automatically extract dates and hours"
                    />
                  </span>
                  <input 
                    type="checkbox" 
                    v-model="enableDocumentUpload"
                    class="addTimeSheet-toggle-checkbox"
                  />
                  <span class="addTimeSheet-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Customer Service Selection (only for consultants with can_work_multiple_customers = true) -->
          <div v-if="showCustomerServiceSelection" class="addTimeSheet-form-group addTimeSheet-form-group-full">
            <label for="customerService">Customer Service <span class="required-asterisk">*</span></label>
            <select
              id="customerService"
              :value="customerServiceSelectValue"
              class="addTimeSheet-select"
              :disabled="loadingCustomerServices"
              @change="onCustomerServiceChange($event.target.value)"
            >
              <option disabled value="">Select Customer Service</option>
              <option v-if="loadingCustomerServices" disabled value="">Loading services...</option>
              <option v-for="service in customerServices" :key="service.id" :value="String(service.id)">
                {{ getCustomerServiceDisplayName(service) }}
              </option>
            </select>
            <div v-if="loadingCustomerServices" class="addTimeSheet-helper-text">
              Loading customer services...
            </div>
            <div v-else-if="customerServices.length === 0 && selectedConsultant" class="addTimeSheet-error-text">
              No customer services found for this consultant. Please configure customer services first.
            </div>
          </div>

          <div class="addTimeSheet-form-group addTimeSheet-form-group-narrow">
            <div class="addTimeSheet-field-label-wrap">
              <LabelWithHelp
                for-id="fromDate"
                label="From Date"
                :required="true"
                :help-text="consultantTimesheetCadenceTooltipText"
              />
            </div>
            <div class="addTimeSheet-input-wrapper">
              <DatePicker 
                id="fromDate"
                v-model="newTimeSheetFromDateUI" 
                placeholder="MM/DD/YYYY"
                :max="maxAllowedDateUI"
              />
            </div>
            <span v-if="!isModalDateRangeValid" class="addTimeSheet-error-text">
              From Date cannot be after To Date
            </span>
            <span class="addTimeSheet-helper-text">
              Only past dates allowed (before today)
            </span>
          </div>

          <div class="addTimeSheet-form-group addTimeSheet-form-group-narrow">
            <div class="addTimeSheet-field-label-wrap">
              <LabelWithHelp
                for-id="toDate"
                label="To Date"
                :required="true"
                :help-text="consultantTimesheetCadenceTooltipText"
              />
            </div>
            <div class="addTimeSheet-input-wrapper">
              <DatePicker 
                id="toDate"
                v-model="newTimeSheetToDateUI" 
                placeholder="MM/DD/YYYY"
                :max="maxAllowedDateUI"
              />
            </div>
            <span v-if="!isModalDateRangeValid" class="addTimeSheet-error-text">
              To Date cannot be before From Date
            </span>
            <span class="addTimeSheet-helper-text">
              Only past dates allowed (before today)
            </span>
          </div>

          <div class="addTimeSheet-form-group addTimeSheet-form-group-narrow">
            <label for="timesheetHours">Hours <span class="required-asterisk">*</span></label>
            <input
              id="timesheetHours"
              type="number"
              min="0"
              step="0.25"
              class="addTimeSheet-input"
              :value="newTimeSheet.hours"
              placeholder="Total hours"
              @input="updateTotalHours($event.target.value)"
            />
          </div>

          <div class="addTimeSheet-form-group addTimeSheet-form-group-full addTimeSheet-form-group-hours">
            <div class="addTimeSheet-hours-header">
              <label>
                Day-wise hours
                <span class="required-asterisk">*</span>
              </label>
              <div class="addTimeSheet-hours-header-meta">
                <div
                  v-if="effectiveTimesheetFrequencyName"
                  class="addTimeSheet-hours-cadence"
                  :title="`Timesheet frequency for this consultant: ${effectiveTimesheetFrequencyName}`"
                >
                  <span class="addTimeSheet-hours-cadence-label">Timesheet frequency</span>
                  <span class="addTimeSheet-hours-cadence-value">{{ effectiveTimesheetFrequencyName }}</span>
                </div>
              </div>
            </div>
            <p v-if="!dailyRows.length" class="addTimeSheet-helper-text">
              Select From Date and To Date to enter hours for each day.
            </p>
            <div v-if="dailyRows.length" class="addTimeSheet-daily-table-wrap">
              <table class="addTimeSheet-daily-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Work Description</th>
                    <th>Regular Hours</th>
                    <th>Overtime Hours</th>
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in dailyRows" :key="row.date">
                    <td class="addTimeSheet-daily-date-cell">
                      <span class="addTimeSheet-daily-date">{{ formatIsoToUs(row.date) }}</span>
                      <span class="addTimeSheet-daily-day">{{ row.day }}</span>
                    </td>
                    <td>
                      <input
                        type="text"
                        class="addTimeSheet-input addTimeSheet-daily-input"
                        :value="row.workDescription"
                        placeholder="Work done"
                        @input="updateDailyField(index, 'workDescription', $event.target.value)"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        class="addTimeSheet-input addTimeSheet-daily-input"
                        :value="row.regularHours"
                        placeholder="0"
                        @input="updateDailyField(index, 'regularHours', $event.target.value)"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        class="addTimeSheet-input addTimeSheet-daily-input"
                        :value="row.overtimeHours"
                        placeholder="0"
                        @input="updateDailyField(index, 'overtimeHours', $event.target.value)"
                      />
                    </td>
                    <td class="addTimeSheet-daily-total">{{ displayDailyTotal(row) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="4">Total hours</td>
                    <td class="addTimeSheet-daily-total">{{ displayHours(dailyHoursTotal) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Upload File for Extraction (shown when toggle is enabled) -->
          <div v-if="enableDocumentUpload" class="addTimeSheet-form-group addTimeSheet-form-group-full addTimeSheet-upload-section">
            <LabelWithHelp
              for-id="timesheetUploadFile"
              label="Upload Time Sheet Document"
              help-text="Upload a PDF or image file to extract dates and hours automatically. This file will be automatically included as an attachment."
              :required="true"
            />
            <div class="addTimeSheet-attach-wrapper">
              <input 
                id="timesheetUploadFile"
                type="file" 
                @change="handleExtractionFileUpload" 
                class="addTimeSheet-file-input" 
                ref="timesheetUploadInput"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
              />
              <div class="addTimeSheet-attach-controls">
                <button 
                  type="button" 
                  class="addTimeSheet-attach-btn" 
                  @click="$refs.timesheetUploadInput.click()"
                >
                  <i class="bi bi-cloud-upload"></i> Upload Document
                </button>
              </div>
              
              <!-- Display uploaded extraction file -->
              <div v-if="extractionFile" class="addTimeSheet-attachments-list">
                <div class="addTimeSheet-attachment-item">
                  <div class="addTimeSheet-attachment-info">
                    <span class="addTimeSheet-attachment-name">{{ extractionFile.name }}</span>
                    <span class="addTimeSheet-attachment-size">({{ formatFileSize(extractionFile.size) }})</span>
                  </div>
                  
                  <!-- File preview -->
                  <div v-if="extractionFilePreviewUrl" class="addTimeSheet-attachment-preview">
                    <template v-if="isImageFile(extractionFile)">
                      <a :href="extractionFilePreviewUrl" target="_blank" class="addTimeSheet-preview-link">
                        <img :src="extractionFilePreviewUrl" alt="Preview" class="addTimeSheet-preview-image" />
                      </a>
                    </template>
                    <template v-else>
                      <a :href="extractionFilePreviewUrl" target="_blank" class="addTimeSheet-preview-link">
                        <i class="bi bi-eye"></i> Preview
                      </a>
                    </template>
                  </div>
                  
                  <div class="addTimeSheet-attachment-actions">
                    <!-- Show Extract Data button only if data hasn't been extracted yet and not failed -->
                    <button 
                      v-if="!hasExtractedData && !extractionFailed"
                      type="button" 
                      class="addTimeSheet-extract-btn" 
                      @click="extractTimesheetDataFromFile(extractionFile, 0)"
                      :disabled="isExtracting"
                      title="Extract dates and hours from this document"
                    >
                      <i class="bi bi-magic"></i> {{ isExtracting ? 'Extracting...' : 'Extract Data' }}
                    </button>
                    <!-- Show success indicator if data has been extracted -->
                    <span 
                      v-else-if="hasExtractedData"
                      class="addTimeSheet-extracted-badge"
                      title="Data has been extracted from this document"
                    >
                      <i class="bi bi-check-circle"></i> Data Extracted
                    </span>
                    <!-- Show retry button if extraction failed -->
                    <button 
                      v-else-if="extractionFailed"
                      type="button" 
                      class="addTimeSheet-extract-btn addTimeSheet-retry-btn" 
                      @click="extractTimesheetDataFromFile(extractionFile, 0)"
                      :disabled="isExtracting"
                      title="Retry data extraction"
                    >
                      <i class="bi bi-arrow-clockwise"></i> {{ isExtracting ? 'Retrying...' : 'Retry Extraction' }}
                    </button>
                    <button 
                      type="button" 
                      class="addTimeSheet-remove-file-btn" 
                      @click="removeExtractionFile"
                      title="Remove file"
                    >
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
                
                <!-- Show extraction failure message with suggestion -->
                <div v-if="extractionFailed" class="addTimeSheet-extraction-failed-message">
                  <div class="addTimeSheet-extraction-failed-icon">
                    <i class="bi bi-exclamation-triangle"></i>
                  </div>
                  <div class="addTimeSheet-extraction-failed-content">
                    <strong>Data extraction was unsuccessful</strong>
                    <p v-if="extractionErrorMessage">{{ extractionErrorMessage }}</p>
                    <p v-else>Unable to extract dates and hours from this document.</p>
                    <p class="addTimeSheet-extraction-suggestion">
                      <i class="bi bi-info-circle"></i> 
                      <strong>Suggestion:</strong> Please enter the timesheet details manually (From Date, To Date, and Hours) in the form fields above. The document will still be attached to the timesheet.
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="!extractionFile" class="addTimeSheet-no-files">
                <span class="addTimeSheet-helper-text">No document uploaded</span>
              </div>
            </div>
          </div>

          <div class="addTimeSheet-form-group addTimeSheet-form-group-full">
            <LabelWithHelp
              for-id="attachments"
              label="Attachments"
              :help-text="enableDocumentUpload ? 'Files that will be attached to this time sheet. The uploaded document above will be automatically included.' : ''"
            />
            <div class="addTimeSheet-attach-wrapper">
              <input 
                id="attachments"
                type="file" 
                @change="handleFileUpload" 
                class="addTimeSheet-file-input" 
                ref="fileInput"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                multiple
              />
              <div class="addTimeSheet-attach-controls">
                <button 
                  type="button" 
                  class="addTimeSheet-attach-btn" 
                  @click="$refs.fileInput.click()"
                >
                  <i class="bi bi-paperclip"></i> Attach files
                </button>
              </div>
              
              <!-- Display all attachments (extraction file + regular attachments) -->
              <div v-if="(enableDocumentUpload && extractionFile) || newTimeSheet.attachments.length > 0" class="addTimeSheet-attachments-list">
                <!-- Extraction file (automatically included when toggle is enabled) -->
                <div v-if="enableDocumentUpload && extractionFile" class="addTimeSheet-attachment-item addTimeSheet-attachment-extraction">
                  <div class="addTimeSheet-attachment-info">
                    <span class="addTimeSheet-attachment-name">{{ extractionFile.name }}</span>
                    <span class="addTimeSheet-attachment-size">({{ formatFileSize(extractionFile.size) }})</span>
                  </div>
                  
                  <!-- File preview -->
                  <div v-if="extractionFilePreviewUrl" class="addTimeSheet-attachment-preview">
                    <template v-if="isImageFile(extractionFile)">
                      <a :href="extractionFilePreviewUrl" target="_blank" class="addTimeSheet-preview-link">
                        <img :src="extractionFilePreviewUrl" alt="Preview" class="addTimeSheet-preview-image" />
                      </a>
                    </template>
                    <template v-else>
                      <a :href="extractionFilePreviewUrl" target="_blank" class="addTimeSheet-preview-link">
                        <i class="bi bi-eye"></i> Preview
                      </a>
                    </template>
                  </div>
                  
                  <span class="addTimeSheet-attachment-auto" title="This file is automatically included from the Upload Document section">
                    <i class="bi bi-check-circle"></i> Auto-included
                  </span>
                </div>
                
                <!-- Regular attachments -->
                <div 
                  v-for="(attachment, index) in newTimeSheet.attachments" 
                  :key="index"
                  class="addTimeSheet-attachment-item"
                >
                  <div class="addTimeSheet-attachment-info">
                    <span class="addTimeSheet-attachment-name">{{ attachment.name }}</span>
                    <span class="addTimeSheet-attachment-size">({{ formatFileSize(attachment.size) }})</span>
                  </div>
                  
                  <!-- File preview -->
                  <div v-if="attachmentPreviewUrls[index]" class="addTimeSheet-attachment-preview">
                    <template v-if="isImageFile(attachment)">
                      <a :href="attachmentPreviewUrls[index]" target="_blank" class="addTimeSheet-preview-link">
                        <img :src="attachmentPreviewUrls[index]" alt="Preview" class="addTimeSheet-preview-image" />
                      </a>
                    </template>
                    <template v-else>
                      <a :href="attachmentPreviewUrls[index]" target="_blank" class="addTimeSheet-preview-link">
                        <i class="bi bi-eye"></i> Preview
                      </a>
                    </template>
                  </div>
                  
                  <button 
                    type="button" 
                    class="addTimeSheet-remove-file-btn" 
                    @click="removeFile(index)"
                    title="Remove file"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>
              
              <div v-if="(!enableDocumentUpload || !extractionFile) && newTimeSheet.attachments.length === 0" class="addTimeSheet-no-files">
                <span class="addTimeSheet-helper-text">
                  {{ enableDocumentUpload ? 'No files attached. Upload a document above to include it as an attachment.' : 'No files selected' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="addTimeSheet-form-actions">
          <button 
            type="button" 
            class="addTimeSheet-btn addTimeSheet-btn-secondary" 
            @click="goBack"
            :disabled="isSubmitting"
          >
            <i class="bi bi-x-lg"></i> Close
          </button>
          <button 
            type="submit" 
            class="addTimeSheet-btn addTimeSheet-btn-primary" 
            :disabled="isSubmitting || !isFormValid"
          >
            <i class="bi bi-check-lg"></i>
            {{ isSubmitting ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Time Sheet') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Error Modal -->
    <ErrorModal
      :show="showErrorModal"
      :title="errorTitle"
      :message="errorMessage"
      button-text="OK"
      @close="closeErrorModal"
    />

    <!-- Success Modal -->
    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      message="Time sheet created successfully!"
      button-text="OK"
      @close="closeSuccessModal"
    />
    
    <!-- Loading Overlay -->
    <LoadingOverlay 
      :show="isExtracting" 
      title="Extracting Data..."
      message="Please wait while we extract dates and hours from your document"
    />
    
    <div class="addTimeSheet-resize-handle" @mousedown.stop="startResize"></div>
  </div>
</template>

<script>
import '../styles/AddTimeSheet.css';
import DatePicker from '@/components/DatePicker.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service.js';
import { notifyTimesheetCreatedWithCompanyMessaging } from '@/services/timesheetCreatedNotification.service';
import { canEditConsultantTimesheet } from '@/modules/consultant/utils/canEditConsultantTimesheet';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import LabelWithHelp from '../components/LabelWithHelp.vue';
import LoadingOverlay from '../components/LoadingOverlay.vue';
import LabelDropdown from '../components/LabelDropdown.vue';
import { buildConsultantDropdownOption } from '@/modules/customer/utils/consultantDropdownLabel';

export default {
  name: 'AddTimeSheet',
  components: { DatePicker, ErrorModal, SuccessModal, LabelWithHelp, LoadingOverlay, LabelDropdown },
  props: {
    prefilledConsultant: {
      type: Object,
      default: null
    },
    initialUploadMode: {
      type: Boolean,
      default: false
    },
    initialFile: {
      type: File,
      default: null
    },
    timesheetToEdit: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      enableDocumentUpload: false, // Toggle for upload document section (will be set from prop in mounted)
      newTimeSheet: {
        consultantName: '',
        consultantId: null,
        fromDate: '',
        toDate: '',
        hours: '',
        status: 'Time Sheet Created',
        companyId: null, // Company ID for the timesheet
        attachments: [], // Changed to array for multiple attachments
        consultantCustomerServiceId: null // For consultants with can_work_multiple_customers
      },
      consultants: [],
      customerServices: [], // Customer services for selected consultant
      selectedConsultant: null, // Currently selected consultant object
      loadingCustomerServices: false,
      attachmentPreviewUrls: {}, // Object to store preview URLs for multiple files
      isSubmitting: false,
      error: '',
      showErrorModal: false,
      errorTitle: 'Validation Error',
      errorMessage: '',
      showSuccessModal: false,
      uploadedTimesheetFile: null, // File for upload (PDF or image) - DEPRECATED
      uploadedFilePreviewUrl: null, // Preview URL for uploaded image - DEPRECATED
      extractionFile: null, // File used for extraction in Upload mode
      extractionFilePreviewUrl: null, // Preview URL for extraction file
      uploadModeAttachments: [], // Additional attachments in Upload mode (separate from extraction file)
      uploadModePreviewUrls: {}, // Preview URLs for additional attachments
      isExtracting: false, // Flag for data extraction in progress
      extractingFileIndex: null, // Index of file being extracted
      hasExtractedData: false, // Flag to track if data was extracted (to preserve it when file is removed)
      extractionFailed: false, // Flag to track if extraction failed or returned no data
      extractionErrorMessage: '', // Message to show when extraction fails
      isRemovingExtractionFile: false, // Flag to prevent watchers from clearing data during file removal
      // Resize functionality
      modalWidth: 600,
      modalHeight: null, // null means auto height
      isResizing: false,
      hasCustomSize: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      dailyRows: [],
      hasSavedDayWiseHours: false
    };
  },
  computed: {
    // US-format proxies for modal date inputs (UI: MM/DD/YYYY, internal: YYYY-MM-DD)
    newTimeSheetFromDateUI: {
      get() {
        return this.formatIsoToUs(this.newTimeSheet.fromDate);
      },
      set(usDate) {
        this.newTimeSheet.fromDate = this.parseUsToIso(usDate);
      }
    },
    newTimeSheetToDateUI: {
      get() {
        return this.formatIsoToUs(this.newTimeSheet.toDate);
      },
      set(usDate) {
        this.newTimeSheet.toDate = this.parseUsToIso(usDate);
      }
    },
    // Check if modal date range is valid
    isModalDateRangeValid() {
      if (!this.newTimeSheet.fromDate || !this.newTimeSheet.toDate) return true;
      return this.newTimeSheet.fromDate <= this.newTimeSheet.toDate;
    },
    dailyHoursTotal() {
      return this.roundHours(this.dailyRows.reduce((sum, row) => sum + this.rowTotalHours(row), 0));
    },
    // Get yesterday's date in MM/DD/YYYY format (max allowed date)
    maxAllowedDateUI() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const month = String(yesterday.getMonth() + 1).padStart(2, '0');
      const day = String(yesterday.getDate()).padStart(2, '0');
      const year = yesterday.getFullYear();
      return `${month}/${day}/${year}`;
    },
    // Get yesterday's date in YYYY-MM-DD format (for validation)
    maxAllowedDateISO() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const year = yesterday.getFullYear();
      const month = String(yesterday.getMonth() + 1).padStart(2, '0');
      const day = String(yesterday.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    // Get companyId from route parameters
    currentCompanyId() {
      return this.$route?.params?.id || this.$route?.params?.companyId || null;
    },
    isEditMode() {
      return !!(this.timesheetToEdit && this.timesheetToEdit.id);
    },
    // Check if consultant is prefilled (from parent component)
    isConsultantPrefilled() {
      return this.isEditMode || (this.prefilledConsultant && (this.prefilledConsultant.consultantName || this.prefilledConsultant.name));
    },
    // Display name for prefilled consultant (always visible in read-only input, e.g. "Mahitha - C2C")
    displayConsultantName() {
      if (this.isEditMode) return this.newTimeSheet.consultantName || '';
      if (!this.prefilledConsultant) return '';
      const name = this.prefilledConsultant.consultantName || this.prefilledConsultant.name || '';
      const type = this.prefilledConsultant.consultantType;
      return type ? `${name} - ${type}` : name;
    },
    consultantSelectValue: {
      get() {
        return this.newTimeSheet.consultantId != null && this.newTimeSheet.consultantId !== ''
          ? String(this.newTimeSheet.consultantId)
          : '';
      },
      set(value) {
        this.newTimeSheet.consultantId = value === '' || value == null ? null : value;
      }
    },
    consultantDropdownOptions() {
      return [
        { value: '', label: 'Select Consultant', disabled: true },
        ...(this.consultants || []).map((consultant) =>
          buildConsultantDropdownOption(consultant, this.consultants)
        )
      ];
    },
    // Check if customer service selection should be shown
    showCustomerServiceSelection() {
      return this.selectedConsultant && this.selectedConsultant.can_work_multiple_customers === true;
    },
    customerServiceSelectValue() {
      const id = this.newTimeSheet.consultantCustomerServiceId;
      return id == null || id === '' ? '' : String(id);
    },
    /** Selected agreement row when consultant works with multiple customers */
    selectedCustomerService() {
      if (!this.showCustomerServiceSelection || !this.newTimeSheet.consultantCustomerServiceId) return null;
      const id = String(this.newTimeSheet.consultantCustomerServiceId);
      return (this.customerServices || []).find(s => String(s.id) === id) || null;
    },
    /**
     * TimesheetFrequency.name from ConsultantCustomerService (multi) or Consultant (single).
     * If neither has timesheetFrequencyId / nested name, dates behave as before (any range).
     */
    effectiveTimesheetFrequencyName() {
      if (!this.selectedConsultant) return null;
      if (this.showCustomerServiceSelection) {
        if (!this.newTimeSheet.consultantCustomerServiceId) return null;
        const svc = this.selectedCustomerService;
        const n = svc?.timesheetFrequency?.name;
        if (n) return String(n).trim();
        return null;
      }
      const n = this.selectedConsultant.timesheetFrequency?.name;
      if (n) return String(n).trim();
      return null;
    },
    /** When true, From/To are aligned to a known cadence (Weekly, Bi-Weekly, Semi-Monthly, Monthly, Quarterly). */
    enforcesTimesheetFrequencyPeriod() {
      const n = this.effectiveTimesheetFrequencyName;
      return !!(n && this.getTimesheetFrequencyKind(n) !== 'none');
    },
    /** Tooltip (From / To labels): consultant’s saved TimesheetFrequency.name + period rules + past-only note */
    consultantTimesheetCadenceTooltipText() {
      const base = 'Only past dates allowed (before today).';
      const cadence = this.effectiveTimesheetFrequencyName;
      if (!cadence) {
        return base;
      }
      const detail = this.timesheetFrequencyPeriodHintText;
      return `Timesheet cadence: ${cadence}. ${detail} ${base}`;
    },
    timesheetFrequencyPeriodHintText() {
      const name = this.effectiveTimesheetFrequencyName;
      if (!name) return '';
      const kind = this.getTimesheetFrequencyKind(name);
      if (kind === 'none') {
        return 'Choose any valid past From/To range. The app only auto-aligns dates for Weekly, Bi-Weekly, Semi-Monthly, Monthly, and Quarterly labels.';
      }
      if (kind === 'weekly') {
        return 'From and To must span exactly one calendar week (7 days, inclusive). Changing From updates To automatically.';
      }
      if (kind === 'biweekly') {
        return 'From and To must span exactly two calendar weeks (14 days, inclusive). Changing From updates To automatically.';
      }
      if (kind === 'semimonthly') {
        return 'The period must be either the 1st through the 15th of a month, or the 16th through the last day of that month. Dates adjust when you change From.';
      }
      if (kind === 'monthly') {
        return 'The period must be the full calendar month. From is set to the 1st and To to the last day of the month you pick.';
      }
      if (kind === 'quarterly') {
        return 'The period must be a full calendar quarter (e.g. Jan 1 – Mar 31). Dates adjust when you change From.';
      }
      return 'To Date is set from From Date according to this cadence.';
    },
    // Validate form (check if all required fields are filled)
    isFormValid() {
      // Check consultant
      if (!this.newTimeSheet.consultantName) return false;
      
      // Check dates
      if (!this.newTimeSheet.fromDate || !this.newTimeSheet.toDate) return false;
      
      // Check date range validity
      if (this.newTimeSheet.fromDate > this.newTimeSheet.toDate) return false;
      
      // Check dates are not in future
      if (this.newTimeSheet.fromDate > this.maxAllowedDateISO || 
          this.newTimeSheet.toDate > this.maxAllowedDateISO) return false;
      
      // Check hours
      if (!(Number(this.newTimeSheet.hours) > 0 || this.dailyHoursTotal > 0)) {
        return false;
      }
      
      // Check customer service if required
      if (this.showCustomerServiceSelection && !this.newTimeSheet.consultantCustomerServiceId) {
        return false;
      }

      if (this.enforcesTimesheetFrequencyPeriod && this.newTimeSheet.fromDate && this.newTimeSheet.toDate) {
        const aligned = this.computeAlignedPeriodForTimesheetFrequency(this.newTimeSheet.fromDate);
        if (aligned && (this.newTimeSheet.fromDate !== aligned.fromIso || this.newTimeSheet.toDate !== aligned.toIso)) {
          return false;
        }
      }
      
      // Check extraction file is uploaded if toggle is enabled
      if (this.enableDocumentUpload && !this.extractionFile && !this.isEditMode) return false;
      
      return true;
    }
  },
  watch: {
    // Watch for initialUploadMode prop to set toggle immediately
    initialUploadMode: {
      immediate: true,
      handler(newVal, oldVal) {
        logger.log('🔍 initialUploadMode watcher triggered:', { newVal, oldVal, currentValue: this.enableDocumentUpload });
        if (newVal === true) {
          this.enableDocumentUpload = true;
          logger.log('🔍 initialUploadMode watcher: Set enableDocumentUpload to true');
        } else if (newVal === false && oldVal === true) {
          // Only reset if it was previously true and now false
          this.enableDocumentUpload = false;
          logger.log('🔍 initialUploadMode watcher: Set enableDocumentUpload to false');
        }
      }
    },
    // Watch for changes in modal dates
    'newTimeSheet.fromDate'(newFromDate) {
      // Check if date is in the future
      if (newFromDate && newFromDate > this.maxAllowedDateISO) {
        this.showDateValidationError('From Date cannot be today or a future date. Please select a past date.');
        this.newTimeSheet.fromDate = '';
        return;
      }

      if (this.enforcesTimesheetFrequencyPeriod && !newFromDate) {
        this.newTimeSheet.toDate = '';
        this.syncDailyRowsFromPeriod();
        return;
      }

      if (this.enforcesTimesheetFrequencyPeriod && newFromDate) {
        this.$nextTick(() => this.applyAlignedPeriodIfFrequencyEnforced(newFromDate));
        this.syncDailyRowsFromPeriod();
        return;
      }
      
      if (newFromDate && this.newTimeSheet.toDate && newFromDate > this.newTimeSheet.toDate) {
        this.newTimeSheet.toDate = '';
      }
      this.syncDailyRowsFromPeriod();
    },
    'newTimeSheet.toDate'(newToDate) {
      // Check if date is in the future
      if (newToDate && newToDate > this.maxAllowedDateISO) {
        this.showDateValidationError('To Date cannot be today or a future date. Please select a past date.');
        this.newTimeSheet.toDate = '';
        return;
      }

      if (this.enforcesTimesheetFrequencyPeriod && newToDate && this.newTimeSheet.fromDate) {
        const aligned = this.computeAlignedPeriodForTimesheetFrequency(this.newTimeSheet.fromDate);
        if (aligned && newToDate !== aligned.toIso) {
          this.showDateValidationError(
            `To Date must match ${this.effectiveTimesheetFrequencyName} (${this.formatIsoToUs(aligned.fromIso)} – ${this.formatIsoToUs(aligned.toIso)}).`
          );
          this.$nextTick(() => {
            this.newTimeSheet.toDate = aligned.toIso;
            this.syncDailyRowsFromPeriod();
          });
        }
        this.syncDailyRowsFromPeriod();
        return;
      }
      
      if (newToDate && this.newTimeSheet.fromDate && newToDate < this.newTimeSheet.fromDate) {
        this.newTimeSheet.fromDate = '';
      }
      this.syncDailyRowsFromPeriod();
    },
    'newTimeSheet.consultantCustomerServiceId'() {
      if (this.enforcesTimesheetFrequencyPeriod && this.newTimeSheet.fromDate) {
        this.$nextTick(() => this.applyAlignedPeriodIfFrequencyEnforced(this.newTimeSheet.fromDate));
      }
    },
    'newTimeSheet.attachments': {
      handler(newAttachments) {
        // Clean up old preview URLs
        Object.values(this.attachmentPreviewUrls).forEach(url => {
          if (url) URL.revokeObjectURL(url);
        });
        this.attachmentPreviewUrls = {};
        
        // Create new preview URLs
        newAttachments.forEach((file, index) => {
          if (file) {
            this.attachmentPreviewUrls[index] = URL.createObjectURL(file);
          }
        });
      },
      deep: true
    },
    // Watch for prefilled consultant data
    prefilledConsultant: {
      immediate: true,
      handler(newVal) {
        logger.log('🔍 AddTimeSheet prefilledConsultant watcher triggered with:', newVal);
        if (newVal && (newVal.consultantName || newVal.name)) {
          logger.log('🔍 Pre-filling consultant data:', newVal);
          this.preFillConsultant(newVal);
        } else {
          logger.log('🔍 No consultant name found in prefilled data');
          this.selectedConsultant = null;
          this.customerServices = [];
        }
      }
    },
    // Watch for toggle changes to reset extraction file when disabled
    enableDocumentUpload(newVal, oldVal) {
      if (oldVal && !newVal) {
        // Clean up extraction file when toggle is disabled
        this.removeExtractionFile();
      }
    }
  },
  methods: {
    // Pre-fill consultant data
    preFillConsultant(consultantData) {
      logger.log('🔍 preFillConsultant called with:', consultantData);
      const name = consultantData?.consultantName || consultantData?.name;
      if (consultantData && name) {
        logger.log('🔍 Setting consultant name to:', name);
        this.newTimeSheet.consultantName = name;
        logger.log('🔍 newTimeSheet.consultantName after setting:', this.newTimeSheet.consultantName);
        
        // Set selected consultant and fetch customer services if needed
        this.selectedConsultant = consultantData;
        if (consultantData.can_work_multiple_customers === true) {
          this.fetchCustomerServices(consultantData.id || consultantData.consultantId);
        }
        // Optional prefill values from invoice assistant chat flow.
        if (consultantData.fromDate) {
          this.newTimeSheet.fromDate = String(consultantData.fromDate);
        }
        if (consultantData.toDate) {
          this.newTimeSheet.toDate = String(consultantData.toDate);
        }
        if (consultantData.hours != null && consultantData.hours !== '') {
          const h = Number(consultantData.hours);
          if (Number.isFinite(h) && h > 0) {
            this.newTimeSheet.hours = h;
          }
        }
        this.$nextTick(() => this.reconcileExtractedDatesWithTimesheetFrequency());
      } else {
        logger.log('🔍 No valid consultant data provided');
      }
    },
    // Handle consultant selection change
    handleConsultantChange(value) {
      const consultantId = value === '' || value == null
        ? null
        : (value ?? this.newTimeSheet.consultantId);

      if (!consultantId) {
        this.selectedConsultant = null;
        this.newTimeSheet.consultantId = null;
        this.newTimeSheet.consultantName = '';
        this.customerServices = [];
        this.newTimeSheet.consultantCustomerServiceId = null;
        this.newTimeSheet.fromDate = '';
        this.newTimeSheet.toDate = '';
        return;
      }
      
      // Find the selected consultant
      const consultant = this.consultants.find(
        (c) => String(c.id) === String(consultantId)
      );
      
      if (consultant) {
        this.selectedConsultant = consultant;
        this.newTimeSheet.consultantId = consultant.id;
        this.newTimeSheet.consultantName = this.getConsultantDisplayName(consultant);
        this.newTimeSheet.fromDate = '';
        this.newTimeSheet.toDate = '';
        // If consultant can work with multiple customers, fetch customer services
        if (consultant.can_work_multiple_customers === true) {
          this.fetchCustomerServices(consultant.id, { resetSelection: true });
        } else {
          this.customerServices = [];
          this.newTimeSheet.consultantCustomerServiceId = null;
        }
      } else {
        this.selectedConsultant = null;
        this.newTimeSheet.consultantId = null;
        this.newTimeSheet.consultantName = '';
        this.customerServices = [];
        this.newTimeSheet.consultantCustomerServiceId = null;
        this.newTimeSheet.fromDate = '';
        this.newTimeSheet.toDate = '';
      }
    },
    onCustomerServiceChange(value) {
      if (value == null || value === '') {
        this.newTimeSheet.consultantCustomerServiceId = null;
        return;
      }
      const matched = (this.customerServices || []).find((s) => String(s.id) === String(value));
      this.newTimeSheet.consultantCustomerServiceId = matched ? matched.id : value;
    },
    // Fetch customer services for a consultant
    async fetchCustomerServices(consultantId, { resetSelection = false } = {}) {
      if (!consultantId) {
        this.customerServices = [];
        return;
      }

      const preservedId = resetSelection ? null : this.newTimeSheet.consultantCustomerServiceId;
      this._customerServicesFetchSeq = (this._customerServicesFetchSeq || 0) + 1;
      const fetchSeq = this._customerServicesFetchSeq;

      this.loadingCustomerServices = true;
      this.customerServices = [];
      if (resetSelection) {
        this.newTimeSheet.consultantCustomerServiceId = null;
      }
      
      try {
        const response = await ApiService.get(`/customer/consultant-customer-services/consultant/${consultantId}`);
        const services = Array.isArray(response.data) ? response.data : [];
        
        // Fetch customer and vendor names for each service
        const servicesWithDetails = await Promise.all(
          services.map(async (service) => {
            let customerName = 'Unknown Customer';
            let vendorName = null;
            
            // Fetch customer name
            if (service.customerId) {
              try {
                const customerResponse = await ApiService.get(`/customer/customers/${service.customerId}`);
                customerName = customerResponse.data?.customerName || customerResponse.data?.name || 'Unknown Customer';
              } catch (error) {
                logger.error('Error fetching customer:', error);
              }
            }
            
            // Fetch vendor name if present
            if (service.preferredVendorId) {
              try {
                const vendorResponse = await ApiService.get(`/vendor/vendors/${service.preferredVendorId}`);
                vendorName = vendorResponse.data?.vendorName || vendorResponse.data?.name || null;
              } catch (error) {
                logger.error('Error fetching vendor:', error);
              }
            }
            
            return {
              ...service,
              customerName,
              vendorName
            };
          })
        );

        if (fetchSeq !== this._customerServicesFetchSeq) return;
        
        const scopedVendorId =
          !this.isEditMode &&
          this.prefilledConsultant?.preferredVendorId != null &&
          this.prefilledConsultant?.preferredVendorId !== ''
            ? String(this.prefilledConsultant.preferredVendorId)
            : null;

        const finalServices = scopedVendorId
          ? servicesWithDetails.filter(s => s?.preferredVendorId != null && String(s.preferredVendorId) === scopedVendorId)
          : servicesWithDetails;

        this.customerServices = finalServices;

        const matched = preservedId != null && preservedId !== ''
          ? this.customerServices.find((s) => String(s.id) === String(preservedId))
          : null;

        if (matched) {
          this.newTimeSheet.consultantCustomerServiceId = matched.id;
        } else if (
          this.customerServices.length === 1 &&
          (this.newTimeSheet.consultantCustomerServiceId == null || this.newTimeSheet.consultantCustomerServiceId === '')
        ) {
          this.newTimeSheet.consultantCustomerServiceId = this.customerServices[0].id;
        } else if (preservedId != null && preservedId !== '') {
          this.newTimeSheet.consultantCustomerServiceId = preservedId;
        }
        logger.log('🔍 Loaded customer services:', this.customerServices);
      } catch (error) {
        logger.error('Error fetching customer services:', error);
        if (fetchSeq === this._customerServicesFetchSeq) {
          this.customerServices = [];
          if (preservedId != null && preservedId !== '') {
            this.newTimeSheet.consultantCustomerServiceId = preservedId;
          }
        }
      } finally {
        if (fetchSeq === this._customerServicesFetchSeq) {
          this.loadingCustomerServices = false;
        }
      }
    },
    // Get display name for customer service (Customer - Vendor format)
    getCustomerServiceDisplayName(service) {
      const customerName = service.customerName || 'Unknown Customer';
      if (service.vendorName) {
        return `${customerName} - ${service.vendorName}`;
      }
      return customerName;
    },
    /** C2C (corp-to-corp) — also accept "CTC" if it appears in data. Vendor line is only for these types. */
    isC2cLikeConsultantType(type) {
      const t = String(type || '').trim().toUpperCase();
      return t === 'C2C' || t === 'CTC';
    },
    /**
     * Company / customer / vendor labels for timesheet-created SMS & email.
     */
    async resolveTimesheetCreatedNotificationDetails(consultant) {
      const effective =
        this.isConsultantPrefilled && this.prefilledConsultant
          ? { ...this.prefilledConsultant, ...(consultant || {}) }
          : consultant;

      let companyName = '';
      const companyId = this.currentCompanyId;
      if (companyId) {
        const fromStore = this.$store.state.currentCompany;
        if (fromStore && String(fromStore.id) === String(companyId)) {
          companyName = String(fromStore.name || fromStore.companyName || '').trim();
        }
        if (!companyName) {
          try {
            const res = await ApiService.get(`/company/${companyId}`);
            const d = res.data;
            companyName = String(d?.name || d?.companyName || '').trim();
          } catch (error) {
            logger.warn('resolveTimesheetCreatedNotificationDetails: company', error);
          }
        }
      }

      let customerName = '';
      let vendorName = '';
      const svc = this.selectedCustomerService;
      const typeForVendor = effective?.consultantType;

      if (svc) {
        customerName = String(svc.customerName || '').trim();
        vendorName = String(svc.vendorName || '').trim();
      } else if (effective) {
        const cid = effective.customerId;
        if (cid) {
          try {
            const cr = await ApiService.get(`/customer/customers/${cid}`);
            customerName = String(cr.data?.customerName || cr.data?.name || '').trim();
          } catch (error) {
            logger.warn('resolveTimesheetCreatedNotificationDetails: customer', error);
          }
        }
        if (this.isC2cLikeConsultantType(typeForVendor) && effective.preferredVendorId) {
          try {
            const vr = await ApiService.get(`/vendor/vendors/${effective.preferredVendorId}`);
            vendorName = String(vr.data?.vendorName || vr.data?.name || '').trim();
          } catch (error) {
            logger.warn('resolveTimesheetCreatedNotificationDetails: vendor', error);
          }
        }
      }

      if (!this.isC2cLikeConsultantType(typeForVendor)) {
        vendorName = '';
      }

      return { companyName, customerName, vendorName };
    },
    // Normalize consultant name (API may return name or consultantName)
    getConsultantDisplayName(consultant) {
      return consultant?.consultantName || consultant?.name || '';
    },
    // Convert YYYY-MM-DD -> MM/DD/YYYY
    formatIsoToUs(isoDate) {
      if (!isoDate) return '';
      const parts = String(isoDate).split('-');
      if (parts.length !== 3) return '';
      const [year, month, day] = parts;
      if (!year || !month || !day) return '';
      return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
    },
    // Convert MM/DD/YYYY -> YYYY-MM-DD
    parseUsToIso(usDate) {
      if (!usDate) return '';
      const parts = String(usDate).split('/');
      if (parts.length !== 3) return '';
      const [month, day, year] = parts.map(p => p.trim());
      if (!year || !month || !day) return '';
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    },
    roundHours(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return 0;
      return Math.round(n * 100) / 100;
    },
    rowTotalHours(row) {
      return this.roundHours((Number(row?.regularHours) || 0) + (Number(row?.overtimeHours) || 0));
    },
    displayHours(value) {
      const n = this.roundHours(value);
      return n ? n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') : '0';
    },
    displayDailyTotal(row) {
      return this.displayHours(this.rowTotalHours(row));
    },
    emptyDailyRow(dateIso) {
      const date = this.parseIsoToLocalDate(dateIso);
      return {
        date: dateIso,
        day: date ? date.toLocaleDateString('en-US', { weekday: 'long' }) : '',
        workDescription: '',
        regularHours: '',
        overtimeHours: ''
      };
    },
    datesInPeriod(fromIso, toIso) {
      const dates = [];
      if (!fromIso || !toIso || fromIso > toIso) return dates;
      let current = fromIso;
      while (current <= toIso) {
        dates.push(current);
        current = this.addCalendarDaysIso(current, 1);
        if (!current) break;
      }
      return dates;
    },
    syncDailyRowsFromPeriod() {
      const fromIso = this.newTimeSheet.fromDate;
      const toIso = this.newTimeSheet.toDate;
      if (!fromIso || !toIso || fromIso > toIso) {
        this.dailyRows = [];
        this.newTimeSheet.hours = '';
        return;
      }
      const previous = new Map(this.dailyRows.map((row) => [row.date, row]));
      this.dailyRows = this.datesInPeriod(fromIso, toIso).map((dateIso) => {
        const existing = previous.get(dateIso);
        return existing ? { ...existing } : this.emptyDailyRow(dateIso);
      });
      this.syncHoursFromDailyRows();
    },
    syncHoursFromDailyRows() {
      const total = this.dailyHoursTotal;
      if (total > 0) {
        this.newTimeSheet.hours = total;
      }
    },
    updateTotalHours(value) {
      this.newTimeSheet.hours = value;
    },
    dayWiseRowsHaveHours(rows) {
      return (rows || []).some((row) => (
        this.roundHours(row.regularHours) > 0 || this.roundHours(row.overtimeHours) > 0
      ));
    },
    updateDailyField(index, field, value) {
      const row = this.dailyRows[index];
      if (!row) return;
      const next = [...this.dailyRows];
      next[index] = { ...row, [field]: value };
      this.dailyRows = next;
      this.syncHoursFromDailyRows();
    },

    parseIsoToLocalDate(isoDate) {
      if (!isoDate) return null;
      const parts = String(isoDate).trim().split('-');
      if (parts.length !== 3) return null;
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
      return new Date(y, m - 1, d);
    },
    formatLocalDateToIso(d) {
      if (!d || Number.isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${month}-${day}`;
    },
    addCalendarDaysIso(isoDate, daysToAdd) {
      const d = this.parseIsoToLocalDate(isoDate);
      if (!d) return '';
      d.setDate(d.getDate() + daysToAdd);
      return this.formatLocalDateToIso(d);
    },
    normalizeTimesheetFrequencyKey(name) {
      return String(name || '')
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
    },
    getTimesheetFrequencyKind(name) {
      const k = this.normalizeTimesheetFrequencyKey(name);
      if (!k) return 'none';
      if (k.includes('biweekly') || (k.includes('bi') && k.includes('week'))) return 'biweekly';
      if (k.includes('weekly')) return 'weekly';
      if (k.includes('semimonthly') || (k.includes('semi') && k.includes('month'))) return 'semimonthly';
      if (k.includes('quarter')) return 'quarterly';
      if (k.includes('month')) return 'monthly';
      return 'none';
    },
    /**
     * Returns { fromIso, toIso } aligned to company TimesheetFrequency name, or null if unknown cadence.
     */
    computeAlignedPeriodForTimesheetFrequency(fromIso) {
      const name = this.effectiveTimesheetFrequencyName;
      if (!fromIso || !name) return null;
      const kind = this.getTimesheetFrequencyKind(name);
      const d = this.parseIsoToLocalDate(fromIso);
      if (!d) return null;

      if (kind === 'weekly') {
        const toIso = this.addCalendarDaysIso(fromIso, 6);
        return { fromIso, toIso };
      }
      if (kind === 'biweekly') {
        const toIso = this.addCalendarDaysIso(fromIso, 13);
        return { fromIso, toIso };
      }
      if (kind === 'semimonthly') {
        const y = d.getFullYear();
        const m = d.getMonth();
        const day = d.getDate();
        const lastDay = new Date(y, m + 1, 0).getDate();
        const toDay = day <= 15 ? 15 : lastDay;
        const toIso = this.formatLocalDateToIso(new Date(y, m, toDay));
        return { fromIso, toIso };
      }
      if (kind === 'monthly') {
        const y = d.getFullYear();
        const m = d.getMonth();
        const fromAligned = new Date(y, m, 1);
        const toAligned = new Date(y, m + 1, 0);
        return {
          fromIso: this.formatLocalDateToIso(fromAligned),
          toIso: this.formatLocalDateToIso(toAligned)
        };
      }
      if (kind === 'quarterly') {
        const y = d.getFullYear();
        const q = Math.floor(d.getMonth() / 3);
        const fromAligned = new Date(y, q * 3, 1);
        const toAligned = new Date(y, q * 3 + 3, 0);
        return {
          fromIso: this.formatLocalDateToIso(fromAligned),
          toIso: this.formatLocalDateToIso(toAligned)
        };
      }
      return null;
    },
    applyAlignedPeriodIfFrequencyEnforced(fromIso) {
      if (!this.enforcesTimesheetFrequencyPeriod || !fromIso) return;
      const aligned = this.computeAlignedPeriodForTimesheetFrequency(fromIso);
      if (!aligned) return;
      if (aligned.toIso > this.maxAllowedDateISO) {
        this.showDateValidationError(
          'This period would end after the latest allowed date. Choose an earlier start date for a full period.'
        );
        this.newTimeSheet.toDate = '';
        return;
      }
      if (aligned.fromIso > this.maxAllowedDateISO) {
        this.showDateValidationError('From Date cannot be today or a future date. Please select a past date.');
        this.newTimeSheet.fromDate = '';
        this.newTimeSheet.toDate = '';
        return;
      }
      if (this.newTimeSheet.fromDate === aligned.fromIso && this.newTimeSheet.toDate === aligned.toIso) {
        return;
      }
      this.newTimeSheet.fromDate = aligned.fromIso;
      this.newTimeSheet.toDate = aligned.toIso;
    },
    reconcileExtractedDatesWithTimesheetFrequency() {
      if (!this.enforcesTimesheetFrequencyPeriod || !this.newTimeSheet.fromDate) return;
      const aligned = this.computeAlignedPeriodForTimesheetFrequency(this.newTimeSheet.fromDate);
      if (!aligned) return;
      if (aligned.toIso > this.maxAllowedDateISO) {
        if (this.$toast) {
          this.$toast.warning('Extracted dates do not fit a full period before today. Clear dates or pick a different start.');
        }
        return;
      }
      const fromMismatch = this.newTimeSheet.fromDate !== aligned.fromIso;
      const toMismatch = this.newTimeSheet.toDate !== aligned.toIso;
      if (fromMismatch || toMismatch) {
        this.newTimeSheet.fromDate = aligned.fromIso;
        this.newTimeSheet.toDate = aligned.toIso;
        if (this.$toast) {
          this.$toast.info(`Dates were adjusted to match ${this.effectiveTimesheetFrequencyName} (${this.formatIsoToUs(aligned.fromIso)} – ${this.formatIsoToUs(aligned.toIso)}).`);
        }
      }
    },

    async fetchConsultants() {
      try {
        const companyId = this.$route.params.id || this.$route.params.companyId;
        logger.log('🔍 fetchConsultants called with companyId:', companyId);
        
        let consultants = [];
        if (this.prefilledConsultant && this.prefilledConsultant.customerId) {
          logger.log('🔍 Fetching consultants for customer:', this.prefilledConsultant.customerId);
          consultants = await ApiService.getConsultantsForCustomer(this.prefilledConsultant.customerId, companyId);
          logger.log('🔍 Loaded customer-specific consultants:', consultants);
        } else if (companyId) {
          consultants = await ApiService.getAllConsultants(companyId);
          logger.log('🔍 Loaded all consultants:', consultants);
        }
        
        // Normalize: ensure each consultant has consultantName (API may return name)
        this.consultants = (Array.isArray(consultants) ? consultants : []).map(c => ({
          ...c,
          consultantName: c.consultantName || c.name || ''
        }));
        
        // When prefilled, ensure the prefilled consultant is in the list (for validation on submit)
        const prefilledName = this.prefilledConsultant?.consultantName || this.prefilledConsultant?.name;
        if (this.prefilledConsultant && prefilledName) {
          const exists = this.consultants.some(c => (c.consultantName || c.name || '') === prefilledName);
          if (!exists) {
            this.consultants.unshift({
              id: this.prefilledConsultant.id || this.prefilledConsultant.consultantId,
              consultantName: prefilledName,
              name: prefilledName,
              consultantType: this.prefilledConsultant.consultantType,
              ...this.prefilledConsultant
            });
            logger.log('🔍 Added prefilled consultant to list:', prefilledName);
          }
        }
      } catch (e) {
        this.consultants = [];
        this.error = 'Failed to load consultants';
        logger.error('🔍 Error fetching consultants:', e);
      }
      this.applyTimesheetForEdit();
    },
    applyTimesheetForEdit() {
      const sheet = this.timesheetToEdit;
      if (!sheet || !sheet.id) return;
      if (!canEditConsultantTimesheet(sheet)) {
        this.showValidationError('This timesheet cannot be edited because it is already invoiced or billed.');
        this.$emit('close');
        return;
      }
      const consultant = (this.consultants || []).find((row) => String(row.id) === String(sheet.consultantId));
      this.selectedConsultant = consultant || {
        id: sheet.consultantId,
        consultantName: '',
        can_work_multiple_customers: !!sheet.consultantCustomerServiceId
      };
      const name = this.selectedConsultant.consultantName || this.selectedConsultant.name || '';
      const type = this.selectedConsultant.consultantType;
      this.newTimeSheet.consultantName = type && name ? `${name} - ${type}` : name;
      this.newTimeSheet.consultantId = sheet.consultantId;
      this.newTimeSheet.fromDate = String(sheet.fromDate || '').slice(0, 10);
      this.newTimeSheet.toDate = String(sheet.toDate || '').slice(0, 10);
      this.newTimeSheet.hours = sheet.hours;
      this.newTimeSheet.status = sheet.status || 'Time Sheet Created';
      this.newTimeSheet.consultantCustomerServiceId = sheet.consultantCustomerServiceId ?? null;
      const needsCustomerService =
        this.selectedConsultant.can_work_multiple_customers === true ||
        this.newTimeSheet.consultantCustomerServiceId != null;
      if (needsCustomerService) {
        this.selectedConsultant = {
          ...this.selectedConsultant,
          can_work_multiple_customers: true
        };
        this.fetchCustomerServices(this.selectedConsultant.id);
      }
      const savedRows = Array.isArray(sheet.dayWiseHours) ? sheet.dayWiseHours : [];
      this.hasSavedDayWiseHours = this.dayWiseRowsHaveHours(savedRows);
      this.$nextTick(() => {
        this.syncDailyRowsFromPeriod();
        if (!savedRows.length) return;
        const byDate = new Map(savedRows.map((row) => [String(row.date || '').slice(0, 10), row]));
        this.dailyRows = this.dailyRows.map((row) => {
          const saved = byDate.get(row.date);
          if (!saved) return row;
          return {
            ...row,
            workDescription: saved.workDescription || '',
            regularHours: saved.regularHours ?? '',
            overtimeHours: saved.overtimeHours ?? ''
          };
        });
        this.syncHoursFromDailyRows();
      });
    },
    isImageFile(file) {
      if (!file) return false;
      const type = file.type || '';
      return type.startsWith('image/');
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        // Add new files to existing attachments
        files.forEach(file => {
          // Check if file already exists (by name and size)
          const exists = this.newTimeSheet.attachments.some(
            existing => existing.name === file.name && existing.size === file.size
          );
          if (!exists) {
            this.newTimeSheet.attachments.push(file);
          }
        });
        logger.log('Files selected for upload:', files);
      }
    },
    removeFile(index) {
      if (index >= 0 && index < this.newTimeSheet.attachments.length) {
        // Clean up preview URL
        if (this.attachmentPreviewUrls[index]) {
          URL.revokeObjectURL(this.attachmentPreviewUrls[index]);
          delete this.attachmentPreviewUrls[index];
        }
        
        // Remove file from array
        this.newTimeSheet.attachments.splice(index, 1);
        
        // Rebuild preview URLs object to maintain proper indexing
        const newPreviewUrls = {};
        this.newTimeSheet.attachments.forEach((file, newIndex) => {
          newPreviewUrls[newIndex] = URL.createObjectURL(file);
        });
        this.attachmentPreviewUrls = newPreviewUrls;
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    goBack() {
      this.resetForm();
      this.$emit('close');
    },
    closeErrorModal() {
      this.showErrorModal = false;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.$emit('success');
    },
    // Public method to reset form (can be called from parent)
    resetFormData() {
      this.resetForm();
    },
    showValidationError(message) {
      this.errorTitle = 'Validation Error';
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    showDateValidationError(message) {
      this.errorTitle = 'Invalid Date';
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    showSuccessMessage() {
      this.showSuccessModal = true;
    },
    resetForm() {
      // Preserve prefilled consultant name if it exists
      const currentConsultantName = this.newTimeSheet.consultantName;
      
      this.newTimeSheet = {
        consultantName: currentConsultantName || '', // Keep current consultant name if prefilled
        consultantId: null,
        fromDate: '',
        toDate: '',
        hours: '',
        status: 'Time Sheet Created',
        companyId: this.currentCompanyId, // Set companyId from route parameters
        attachments: [],
        consultantCustomerServiceId: null
      };
      this.dailyRows = [];
      this.hasSavedDayWiseHours = false;
      
      // Reset customer services if consultant changed
      if (!this.isConsultantPrefilled) {
        this.selectedConsultant = null;
        this.customerServices = [];
      }
      
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      
      // Reset extraction file and toggle (but preserve if set from prop)
      // IMPORTANT: Only reset if initialUploadMode is false
      if (!this.initialUploadMode) {
        this.enableDocumentUpload = false;
        this.hasExtractedData = false; // Reset extracted data flag on form reset
        this.removeExtractionFile();
      } else {
        // If initialUploadMode is true, ensure toggle is enabled
        this.enableDocumentUpload = true;
        // Preserve initial file if it exists
        if (this.initialFile && !this.extractionFile) {
          this.extractionFile = this.initialFile;
          if (!this.extractionFilePreviewUrl) {
            this.extractionFilePreviewUrl = URL.createObjectURL(this.initialFile);
          }
          // Automatically extract data if file is preserved and not already extracted
          if (!this.hasExtractedData) {
            this.$nextTick(() => {
              this.extractTimesheetDataFromFile(this.initialFile, 0);
            });
          }
        }
        logger.log('🔍 resetForm: Preserved enableDocumentUpload = true because initialUploadMode is true');
      }
      
      // Clean up all preview URLs
      Object.values(this.attachmentPreviewUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      this.attachmentPreviewUrls = {};
    },
    // Remove uploaded timesheet file
    removeTimesheetFile() {
      // Clean up preview URL if exists
      if (this.uploadedFilePreviewUrl) {
        URL.revokeObjectURL(this.uploadedFilePreviewUrl);
        this.uploadedFilePreviewUrl = null;
      }
      this.uploadedTimesheetFile = null;
      if (this.$refs.timesheetUploadInput) {
        this.$refs.timesheetUploadInput.value = '';
      }
    },
    // Extract timesheet data (dates and hours) from extraction file
    async extractTimesheetDataFromFile(file, index) {
      if (!file || this.isExtracting) return;
      
      // Use extractionFile if file parameter is not provided
      const fileToExtract = file || this.extractionFile;
      if (!fileToExtract) return;
      
      this.isExtracting = true;
      this.extractingFileIndex = index;
      
      try {
        const formData = new FormData();
        formData.append('file', fileToExtract);
        
        // Call backend API to extract data from the document
        const response = await ApiService.post('/consultant/timesheet/extract', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data && response.data.success) {
          const extractedData = response.data.data;
          
          // Check if ALL three required fields were extracted
          const hasFromDate = extractedData.fromDate && extractedData.fromDate.trim() !== '';
          const hasToDate = extractedData.toDate && extractedData.toDate.trim() !== '';
          const hasHours = extractedData.hours !== null && extractedData.hours !== '' && extractedData.hours !== undefined;
          
          // Reset extraction flags
          this.hasExtractedData = false;
          this.extractionFailed = false;
          this.extractionErrorMessage = '';
          
          // Check if ALL three required fields are present
          const allFieldsExtracted = hasFromDate && hasToDate && hasHours;
          
          if (allFieldsExtracted) {
            // All three fields are present - extraction successful
            this.newTimeSheet.fromDate = extractedData.fromDate;
            this.newTimeSheet.toDate = extractedData.toDate;
            this.newTimeSheet.hours = extractedData.hours;
            this.hasExtractedData = true;
            this.$nextTick(() => this.reconcileExtractedDatesWithTimesheetFrequency());
            
            // Show success message
            if (this.$toast) {
              this.$toast.success('Data extracted successfully from the document!');
            }
            logger.log('Extracted timesheet data (all fields):', extractedData);
          } else {
            // Not all three fields were extracted - treat as unsuccessful
            this.extractionFailed = true;
            
            // Build detailed error message about what's missing
            const missingFields = [];
            if (!hasFromDate) missingFields.push('From Date');
            if (!hasToDate) missingFields.push('To Date');
            if (!hasHours) missingFields.push('Hours');
            
            this.extractionErrorMessage = `Extraction incomplete. Missing required fields: ${missingFields.join(', ')}. Please enter the missing details manually.`;
            
            // Populate any fields that were extracted (partial data)
            if (hasFromDate) {
              this.newTimeSheet.fromDate = extractedData.fromDate;
            }
            if (hasToDate) {
              this.newTimeSheet.toDate = extractedData.toDate;
            }
            if (hasHours) {
              this.newTimeSheet.hours = extractedData.hours;
            }
            this.$nextTick(() => this.reconcileExtractedDatesWithTimesheetFrequency());
            
            // Show warning message
            if (this.$toast) {
              this.$toast.warning('Extraction incomplete. Please enter the missing details manually.');
            }
            logger.warn('Extraction incomplete - missing fields:', missingFields, 'Extracted data:', extractedData);
          }
        } else {
          throw new Error(response.data?.message || 'Failed to extract data');
        }
        
      } catch (e) {
        logger.error('Error extracting timesheet data:', e);
        
        // Set extraction failed flag and message
        this.extractionFailed = true;
        this.hasExtractedData = false;
        this.extractionErrorMessage = e.response?.data?.message || e.message || 'Failed to extract data from the document.';
        
        // Show info message instead of blocking error modal
        if (this.$toast) {
          this.$toast.warning('Data extraction failed. Please enter the details manually.');
        }
        
        // Don't show error modal for extraction failures - show inline message instead
        // this.errorTitle = 'Extraction Error';
        // this.errorMessage = e.response?.data?.message || e.message || 'Failed to extract data from the document. Please enter the information manually.';
        // this.showErrorModal = true;
      } finally {
        this.isExtracting = false;
        this.extractingFileIndex = null;
      }
    },
    // Get file icon based on file type
    getFileIcon(file) {
      if (!file) return 'bi bi-file-earmark';
      const type = file.type || '';
      const name = file.name || '';
      
      if (type === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) {
        return 'bi bi-file-earmark-pdf';
      } else if (type.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name)) {
        return 'bi bi-file-earmark-image';
      }
      return 'bi bi-file-earmark';
    },
    // Get preview URL for image files
    getFilePreviewUrl(file) {
      if (!file || !this.isImageFile(file)) return '';
      if (this.uploadedFilePreviewUrl) {
        URL.revokeObjectURL(this.uploadedFilePreviewUrl);
      }
      this.uploadedFilePreviewUrl = URL.createObjectURL(file);
      return this.uploadedFilePreviewUrl;
    },
    // Handle extraction file upload (single file for OCR)
    handleExtractionFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      const validMimeTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp'
      ];
      
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const fileType = file.type || '';
      
      const isValidExtension = validExtensions.includes(fileExtension);
      const isValidMimeType = validMimeTypes.includes(fileType);
      
      if (!isValidExtension && !isValidMimeType) {
        this.showValidationError('Invalid file type. Please upload a PDF or image file (JPG, PNG, GIF, BMP, or WEBP).');
        if (this.$refs.timesheetUploadInput) {
          this.$refs.timesheetUploadInput.value = '';
        }
        return;
      }
      
      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.showValidationError('File size exceeds 10MB limit. Please upload a smaller file.');
        if (this.$refs.timesheetUploadInput) {
          this.$refs.timesheetUploadInput.value = '';
        }
        return;
      }
      
      // Clean up previous extraction file preview
      if (this.extractionFilePreviewUrl) {
        URL.revokeObjectURL(this.extractionFilePreviewUrl);
      }
      
      this.extractionFile = file;
      logger.log('Extraction file selected:', file.name, file.type);
      
      // Reset extraction flags when a new file is uploaded
      this.hasExtractedData = false;
      this.extractionFailed = false;
      this.extractionErrorMessage = '';
      
      // Create preview URL for all files (images show thumbnail, PDFs show preview link)
      // This allows users to preview both images and PDFs, same as attachments section
      this.extractionFilePreviewUrl = URL.createObjectURL(file);
      
      // Automatically extract data from the uploaded file
      this.$nextTick(() => {
        this.extractTimesheetDataFromFile(file, 0);
      });
      
      // Clear input
      if (this.$refs.timesheetUploadInput) {
        this.$refs.timesheetUploadInput.value = '';
      }
    },
    // Handle additional attachments upload (multiple files)
    handleUploadModeAttachments(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;
      
      const validExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
      const validMimeTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      // Validate and add each file
      files.forEach(file => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const fileType = file.type || '';
        
        const isValidExtension = validExtensions.includes(fileExtension);
        const isValidMimeType = validMimeTypes.includes(fileType);
        
        if (!isValidExtension && !isValidMimeType) {
          this.showValidationError(`Invalid file type: ${file.name}. Please upload a PDF, DOC, DOCX, or image file.`);
          return;
        }
        
        if (file.size > maxSize) {
          this.showValidationError(`File size exceeds 10MB limit: ${file.name}. Please upload a smaller file.`);
          return;
        }
        
        // Check if file already exists
        const exists = this.uploadModeAttachments.some(
          f => f.name === file.name && f.size === file.size
        );
        
        if (!exists) {
          const fileIndex = this.uploadModeAttachments.length;
          this.uploadModeAttachments.push(file);
          
          // Create preview URL if it's an image
          if (this.isImageFile(file)) {
            this.uploadModePreviewUrls[fileIndex] = URL.createObjectURL(file);
          }
          
          logger.log('Additional attachment added:', file.name, file.type);
        }
      });
      
      // Clear input
      if (this.$refs.uploadAttachmentsInput) {
        this.$refs.uploadAttachmentsInput.value = '';
      }
    },
    // Remove extraction file
    // NOTE: When the extraction file is removed, also clear the extracted data (fromDate, toDate, hours)
    // since the data was extracted from that specific file.
    removeExtractionFile() {
      // Clean up preview URL if exists
      if (this.extractionFilePreviewUrl) {
        URL.revokeObjectURL(this.extractionFilePreviewUrl);
        this.extractionFilePreviewUrl = null;
      }
      this.extractionFile = null;
      if (this.$refs.timesheetUploadInput) {
        this.$refs.timesheetUploadInput.value = '';
      }
      
      // Clear extracted data if it was extracted from this file
      // Only clear if we have the flag indicating data was extracted
      if (this.hasExtractedData) {
        this.newTimeSheet.fromDate = '';
        this.newTimeSheet.toDate = '';
        this.newTimeSheet.hours = '';
        this.hasExtractedData = false;
        logger.log('🔍 removeExtractionFile - Cleared extracted data (fromDate, toDate, hours)');
      }
      
      // Reset extraction failure flags
      this.extractionFailed = false;
      this.extractionErrorMessage = '';
    },
    // Check if file can be used for extraction (PDF or image)
    canExtractFromFile(file) {
      if (!file) return false;
      const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(fileExtension);
    },
    // Remove file from upload mode attachments
    removeUploadModeFile(index) {
      if (index >= 0 && index < this.uploadModeAttachments.length) {
        // Clean up preview URL if exists
        if (this.uploadModePreviewUrls[index]) {
          URL.revokeObjectURL(this.uploadModePreviewUrls[index]);
          delete this.uploadModePreviewUrls[index];
        }
        
        this.uploadModeAttachments.splice(index, 1);
        
        // Reindex preview URLs - rebuild mapping for remaining files
        const newPreviewUrls = {};
        this.uploadModeAttachments.forEach((file, newIndex) => {
          // Find the original index of this file
          // Files before the removed index keep their preview URLs
          // Files after the removed index need their preview URLs shifted
          if (newIndex < index) {
            // File is before removed index, keep same preview URL
            if (this.uploadModePreviewUrls[newIndex]) {
              newPreviewUrls[newIndex] = this.uploadModePreviewUrls[newIndex];
            }
          } else {
            // File is after removed index, use preview URL from oldIndex + 1
            const oldIndex = newIndex + 1;
            if (this.uploadModePreviewUrls[oldIndex]) {
              newPreviewUrls[newIndex] = this.uploadModePreviewUrls[oldIndex];
            }
          }
        });
        this.uploadModePreviewUrls = newPreviewUrls;
      }
    },
    // Submit timesheet upload (PDF or image file with form data)
    resolveConsultantForSubmit() {
      if (this.isEditMode) {
        const id = this.timesheetToEdit.consultantId || this.selectedConsultant?.id || this.newTimeSheet.consultantId;
        const fromList = (this.consultants || []).find((row) => String(row.id) === String(id));
        if (fromList) return fromList;
        if (this.selectedConsultant?.id) return this.selectedConsultant;
        if (id) {
          return {
            id,
            can_work_multiple_customers: this.selectedConsultant?.can_work_multiple_customers
          };
        }
        return null;
      }
      if (this.isConsultantPrefilled && this.prefilledConsultant) {
        return {
          id: this.prefilledConsultant.id || this.prefilledConsultant.consultantId,
          can_work_multiple_customers: this.prefilledConsultant.can_work_multiple_customers
        };
      }
      if (this.newTimeSheet.consultantId != null && this.newTimeSheet.consultantId !== '') {
        const byId = (this.consultants || []).find(
          (row) => String(row.id) === String(this.newTimeSheet.consultantId)
        );
        if (byId) return byId;
      }
      return (this.consultants || []).find(
        (row) => (row.consultantName || row.name || '') === this.newTimeSheet.consultantName
      ) || null;
    },
    async submitTimesheetUpload() {
      if (this.isSubmitting || !this.extractionFile) return;
      
      this.isSubmitting = true;
      this.error = '';
      
      // Collect all validation errors
      const errors = [];
      
      // Validate consultant
      if (!this.newTimeSheet.consultantName) {
        errors.push('• Please select a consultant.');
      }
      
      // Validate dates
      if (!this.newTimeSheet.fromDate) {
        errors.push('• Please select a from date.');
      }
      
      if (!this.newTimeSheet.toDate) {
        errors.push('• Please select a to date.');
      }
      
      // Validate hours
      if (!this.newTimeSheet.hours || this.newTimeSheet.hours <= 0) {
        errors.push('• Please enter regular or overtime hours for at least one day.');
      }
      
      // Validate companyId
      if (!this.currentCompanyId) {
        errors.push('• Company ID is required.');
      }
      
      // Resolve consultant
      let consultant = this.resolveConsultantForSubmit();
      if (!consultant || !consultant.id) {
        errors.push('• Please select a valid consultant.');
      }
      
      // Validate customer service selection for consultants with can_work_multiple_customers
      if (consultant && consultant.can_work_multiple_customers === true) {
        if (!this.newTimeSheet.consultantCustomerServiceId) {
          errors.push('• Please select a customer service.');
        }
      }
      
      // Validate date range
      if (this.newTimeSheet.fromDate && this.newTimeSheet.toDate && 
          this.newTimeSheet.fromDate > this.newTimeSheet.toDate) {
        errors.push('• From Date cannot be after To Date.');
      }
      
      // Validate that dates are not in the future
      if (this.newTimeSheet.fromDate && this.newTimeSheet.fromDate > this.maxAllowedDateISO) {
        errors.push('• From Date cannot be today or a future date. Please select a past date.');
      }
      
      if (this.newTimeSheet.toDate && this.newTimeSheet.toDate > this.maxAllowedDateISO) {
        errors.push('• To Date cannot be today or a future date. Please select a past date.');
      }
      
      // Validate extraction file
      if (!this.isEditMode && !this.extractionFile) {
        errors.push('• Please upload a time sheet document (PDF or image) for extraction.');
      } else if (this.extractionFile) {
        const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        const fileExtension = '.' + this.extractionFile.name.split('.').pop().toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
          errors.push('• Invalid extraction file type. Please upload a PDF or image file.');
        }
      }
      
      // If there are validation errors, show them all at once
      if (errors.length > 0) {
        this.showValidationError(errors.join('\n'));
        this.isSubmitting = false;
        return;
      }
      
      // Validate for duplicate timesheet (same consultant, agreement, and period)
      const duplicateCheck = await this.checkForDuplicateTimesheet(consultant);
      if (duplicateCheck.isDuplicate) {
        this.showValidationError(duplicateCheck.message);
        this.isSubmitting = false;
        return;
      }
      
      try {
        const formData = new FormData();
        
        // Add form data
        formData.append('consultantId', consultant.id);
        formData.append('fromDate', this.newTimeSheet.fromDate);
        formData.append('toDate', this.newTimeSheet.toDate);
        formData.append('hours', this.newTimeSheet.hours);
        formData.append('dayWiseHours', JSON.stringify(this.dailyRows));
        formData.append('status', this.newTimeSheet.status || 'Time Sheet Created');
        formData.append('companyId', this.currentCompanyId);
        
        // Add consultantCustomerServiceId if consultant can work with multiple customers
        if (consultant.can_work_multiple_customers === true && this.newTimeSheet.consultantCustomerServiceId) {
          formData.append('consultantCustomerServiceId', this.newTimeSheet.consultantCustomerServiceId);
        }
        
        // Add extraction file as attachment (required) - automatically included by default
        // The file uploaded in "Upload Time Sheet Document" is always included as an attachment
        if (this.extractionFile) {
          if (!(this.extractionFile instanceof File)) {
            logger.error('Extraction file is not a valid File object:', this.extractionFile);
            throw new Error('Invalid extraction file');
          }
          formData.append('attachment', this.extractionFile);
        } else if (!this.isEditMode) {
          logger.error('Extraction file is missing! Cannot submit without extraction file.');
          throw new Error('Extraction file is required');
        }
        
        // Add additional attachments (optional)
        this.uploadModeAttachments.forEach((file, index) => {
          if (file instanceof File) {
            formData.append('attachment', file);
            logger.log(`✓ Added additional attachment ${index + 1}:`, file.name, `(${file.size} bytes)`);
          } else {
            logger.warn('Skipping invalid file at index', index, ':', file);
          }
        });
        
        const totalAttachments = 1 + this.uploadModeAttachments.length;
        logger.log(`📎 Submitting timesheet with ${totalAttachments} total attachment(s):`);
        logger.log(`   - Extraction file: "${this.extractionFile.name}"`);
        logger.log(`   - Additional files: ${this.uploadModeAttachments.length}`);
        
        // Submit with FormData for file upload (using the same endpoint as manual entry)
        const response = this.isEditMode
          ? await ApiService.put(`/consultant/timesheet/${this.timesheetToEdit.id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
          : await ApiService.post('/consultant/timesheet', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
        
        // Emit success event
        if (response.data && response.data.success && response.data.data) {
          this.$emit('success', response.data.data);
          if (!this.isEditMode) {
          const notifyDetails = await this.resolveTimesheetCreatedNotificationDetails(consultant);
          await notifyTimesheetCreatedWithCompanyMessaging({
            isConsultantPrefilled: this.isConsultantPrefilled,
            displayConsultantName: this.displayConsultantName,
            selectedConsultantName: this.newTimeSheet.consultantName,
            consultant,
            fromDate: this.newTimeSheet.fromDate,
            toDate: this.newTimeSheet.toDate,
            hours: this.newTimeSheet.hours,
            status: this.newTimeSheet.status,
            companyId: this.currentCompanyId,
            companyName: notifyDetails.companyName,
            customerName: notifyDetails.customerName,
            vendorName: notifyDetails.vendorName,
          });
          }
          this.showSuccessMessage();
          // Reset form and upload files
          this.resetForm();
          this.removeExtractionFile();
          this.uploadModeAttachments = [];
          // Clean up preview URLs
          Object.values(this.uploadModePreviewUrls).forEach(url => {
            if (url) URL.revokeObjectURL(url);
          });
          this.uploadModePreviewUrls = {};
        } else {
          throw new Error(response.data?.message || 'Upload failed');
        }
        
      } catch (e) {
        this.errorTitle = 'Upload Error';
        this.errorMessage = e.response?.data?.message || e.message || 'Failed to upload time sheet';
        this.showErrorModal = true;
        logger.error('Error uploading timesheet:', e);
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitTimeSheet() {
      if (this.isSubmitting) return;
      
      this.isSubmitting = true;
      this.error = '';
      
      // Collect all validation errors
      const errors = [];
      
      if (!this.newTimeSheet.consultantName) {
        errors.push('• Please select a consultant.');
      }
      
      if (!this.newTimeSheet.fromDate) {
        errors.push('• Please select a from date.');
      }
      
      if (!this.newTimeSheet.toDate) {
        errors.push('• Please select a to date.');
      }
      
      if (!this.newTimeSheet.hours || this.newTimeSheet.hours <= 0) {
        errors.push('• Please enter regular or overtime hours for at least one day.');
      }
      
      // Validate companyId
      if (!this.currentCompanyId) {
        errors.push('• Company ID is required.');
      }
      
      // Resolve consultant: when prefilled, use prefilled data; otherwise find in list
      let consultant = this.resolveConsultantForSubmit();
      if (!consultant || !consultant.id) {
        errors.push('• Please select a valid consultant.');
      }
      
      // Validate customer service selection for consultants with can_work_multiple_customers
      if (consultant && consultant.can_work_multiple_customers === true) {
        if (!this.newTimeSheet.consultantCustomerServiceId) {
          errors.push('• Please select a customer service.');
        }
      }

      // Validate date range
      if (this.newTimeSheet.fromDate && this.newTimeSheet.toDate && 
          this.newTimeSheet.fromDate > this.newTimeSheet.toDate) {
        errors.push('• From Date cannot be after To Date.');
      }
      
      // Validate that dates are not in the future (not today or later)
      if (this.newTimeSheet.fromDate && this.newTimeSheet.fromDate > this.maxAllowedDateISO) {
        errors.push('• From Date cannot be today or a future date. Please select a past date.');
      }
      
      if (this.newTimeSheet.toDate && this.newTimeSheet.toDate > this.maxAllowedDateISO) {
        errors.push('• To Date cannot be today or a future date. Please select a past date.');
      }
      
      // Validate extraction file if toggle is enabled
      if (this.enableDocumentUpload && !this.extractionFile && !this.isEditMode) {
        errors.push('• Please upload a time sheet document for data extraction.');
      }
      
      // If there are validation errors, show them all at once
      if (errors.length > 0) {
        this.showValidationError(errors.join('\n'));
        this.isSubmitting = false;
        return;
      }
      
      // Validate for duplicate timesheet (same consultant, agreement, and period)
      const duplicateCheck = await this.checkForDuplicateTimesheet(consultant);
      if (duplicateCheck.isDuplicate) {
        this.showValidationError(duplicateCheck.message);
        this.isSubmitting = false;
        return;
      }
      
      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('consultantId', consultant.id);
        formData.append('fromDate', this.newTimeSheet.fromDate);
        formData.append('toDate', this.newTimeSheet.toDate);
        formData.append('hours', this.newTimeSheet.hours);
        formData.append('dayWiseHours', JSON.stringify(this.dailyRows));
        formData.append('status', this.newTimeSheet.status);
        
        // Add companyId for the timesheet
        const companyId = this.currentCompanyId;
        if (companyId) {
          formData.append('companyId', companyId);
          this.newTimeSheet.companyId = companyId; // Store in data object as well
        }
        
        // Add consultantCustomerServiceId if consultant can work with multiple customers
        if (consultant.can_work_multiple_customers === true && this.newTimeSheet.consultantCustomerServiceId) {
          formData.append('consultantCustomerServiceId', this.newTimeSheet.consultantCustomerServiceId);
        }

        // Add extraction file first if toggle is enabled
        if (this.enableDocumentUpload && this.extractionFile) {
          formData.append('attachment', this.extractionFile);
        }
        
        // Add all regular attachments
        this.newTimeSheet.attachments.forEach((file) => {
          formData.append('attachment', file);
        });

        // Submit with FormData for file upload
        const response = this.isEditMode
          ? await ApiService.put(`/consultant/timesheet/${this.timesheetToEdit.id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
          : await ApiService.post('/consultant/timesheet', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
        
        // Emit the created timesheet data to parent component
        if (response.data && response.data.success && response.data.data) {
          this.$emit('success', response.data.data);
          if (!this.isEditMode) {
          const notifyDetails = await this.resolveTimesheetCreatedNotificationDetails(consultant);
          await notifyTimesheetCreatedWithCompanyMessaging({
            isConsultantPrefilled: this.isConsultantPrefilled,
            displayConsultantName: this.displayConsultantName,
            selectedConsultantName: this.newTimeSheet.consultantName,
            consultant,
            fromDate: this.newTimeSheet.fromDate,
            toDate: this.newTimeSheet.toDate,
            hours: this.newTimeSheet.hours,
            status: this.newTimeSheet.status,
            companyId: this.currentCompanyId,
            companyName: notifyDetails.companyName,
            customerName: notifyDetails.customerName,
            vendorName: notifyDetails.vendorName,
          });
          }

          this.showSuccessMessage();
          this.resetForm();
        }
      } catch (e) {
        this.errorTitle = 'Error';
        this.errorMessage = e.message || "Failed to submit time sheet";
        this.showErrorModal = true;
      } finally {
        this.isSubmitting = false;
      }
    },
    /**
     * Check for duplicate timesheet (same consultant, agreement, and overlapping period)
     */
    async checkForDuplicateTimesheet(consultant) {
      if (!consultant || !consultant.id || !this.newTimeSheet.fromDate || !this.newTimeSheet.toDate) {
        return { isDuplicate: false, message: '' };
      }

      const toDateKey = (value) => String(value || '').slice(0, 10);
      const currentEditId = this.isEditMode
        ? (this.timesheetToEdit?.id ?? this.timesheetToEdit?.timesheetId)
        : null;

      if (this.isEditMode && this.timesheetToEdit) {
        const sameConsultant = String(consultant.id) === String(this.timesheetToEdit.consultantId || '');
        const sameFrom = toDateKey(this.newTimeSheet.fromDate) === toDateKey(this.timesheetToEdit.fromDate);
        const sameTo = toDateKey(this.newTimeSheet.toDate) === toDateKey(this.timesheetToEdit.toDate);
        if (sameConsultant && sameFrom && sameTo) {
          return { isDuplicate: false, message: '' };
        }
      }
      
      try {
        // Build query parameters
        const params = {
          consultantId: consultant.id
        };
        
        if (this.currentCompanyId) {
          params.companyId = this.currentCompanyId;
        }
        
        // If consultant can work multiple customers, filter by customer service (agreement)
        if (consultant.can_work_multiple_customers === true && this.newTimeSheet.consultantCustomerServiceId) {
          params.consultantCustomerServiceId = this.newTimeSheet.consultantCustomerServiceId;
        }
        
        // Fetch existing timesheets for this consultant and agreement
        const response = await ApiService.get('/consultant/timesheet', { params });
        const payload = response?.data;
        const existingTimesheets = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);
        
        // Check if the new period overlaps with any existing timesheet
        const newFrom = toDateKey(this.newTimeSheet.fromDate);
        const newTo = toDateKey(this.newTimeSheet.toDate);
        
        const duplicate = existingTimesheets.find(existing => {
          const existingId = existing?.id ?? existing?.timesheetId;
          if (currentEditId != null && String(existingId) === String(currentEditId)) {
            return false;
          }
          const existingFrom = toDateKey(existing.fromDate);
          const existingTo = toDateKey(existing.toDate);
          
          // Check for overlap: two periods overlap if (start1 <= end2) && (start2 <= end1)
          const overlaps = newFrom <= existingTo && existingFrom <= newTo;
          
          // Also check if consultantCustomerServiceId matches (for multi-customer consultants)
          if (consultant.can_work_multiple_customers === true) {
            const existingServiceId = existing.consultantCustomerServiceId;
            const newServiceId = this.newTimeSheet.consultantCustomerServiceId;
            
            // If both have service IDs, they must match
            if (existingServiceId && newServiceId) {
              return overlaps && existingServiceId === newServiceId;
            }
            // If one has service ID and other doesn't, they're different agreements
            if ((existingServiceId && !newServiceId) || (!existingServiceId && newServiceId)) {
              return false;
            }
          }
          
          return overlaps;
        });
        
        if (duplicate) {
          const fromDateStr = this.formatIsoToUs(this.newTimeSheet.fromDate);
          const toDateStr = this.formatIsoToUs(this.newTimeSheet.toDate);
          const existingFromStr = this.formatIsoToUs(duplicate.fromDate);
          const existingToStr = this.formatIsoToUs(duplicate.toDate);
          
          let message = `A timesheet already exists for this consultant and period.\n\n`;
          message += `New Period: ${fromDateStr} - ${toDateStr}\n`;
          message += `Existing Period: ${existingFromStr} - ${existingToStr}`;
          
          if (consultant.can_work_multiple_customers === true && this.newTimeSheet.consultantCustomerServiceId) {
            message += `\n\nThis consultant has multiple agreements. The timesheet already exists for the same agreement and period.`;
          }
          
          return { isDuplicate: true, message };
        }
        
        return { isDuplicate: false, message: '' };
      } catch (error) {
        logger.error('Error checking for duplicate timesheet:', error);
        // Don't block creation if check fails, but log the error
        return { isDuplicate: false, message: '' };
      }
    },
    // Resize functionality
    startResize(e) {
      // Only start resize if clicking on the resize handle
      if (!e.target.classList.contains('addTimeSheet-resize-handle')) {
        return;
      }
      e.preventDefault();
      this.isResizing = true;
      this.hasCustomSize = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.modalWidth || 600;
      this.resizeStartHeight = this.modalHeight || (this.$el?.offsetHeight || 600);
      
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
    },
    handleResize(e) {
      if (!this.isResizing) return;
      
      const deltaX = e.clientX - this.resizeStartX;
      const deltaY = e.clientY - this.resizeStartY;
      
      const minWidth = 600;
      const minHeight = 400;
      const maxWidth = window.innerWidth - 100;
      const maxHeight = window.innerHeight - 100;
      
      const newWidth = Math.max(minWidth, Math.min(maxWidth, this.resizeStartWidth + deltaX));
      const newHeight = Math.max(minHeight, Math.min(maxHeight, this.resizeStartHeight + deltaY));
      
      this.modalWidth = newWidth;
      this.modalHeight = newHeight;
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
    }
  },
  created() {
    // Set upload mode early if prop is true
    if (this.initialUploadMode) {
      this.enableDocumentUpload = true;
      logger.log('🔍 AddTimeSheet created: Set enableDocumentUpload to true (initialUploadMode is true)');
    }
    
    // Set initial file if provided
    if (this.initialFile) {
      this.extractionFile = this.initialFile;
      // Create preview URL for the file
      this.extractionFilePreviewUrl = URL.createObjectURL(this.initialFile);
      logger.log('🔍 AddTimeSheet created: Set initial file:', this.initialFile.name);
      
      // Automatically extract data from the initial file
      this.$nextTick(() => {
        this.extractTimesheetDataFromFile(this.initialFile, 0);
      });
    }
  },
  mounted() {
    logger.log('🔍 AddTimeSheet mounted, prefilledConsultant prop:', this.prefilledConsultant);
    logger.log('🔍 AddTimeSheet initialUploadMode prop:', this.initialUploadMode);
    logger.log('🔍 AddTimeSheet initialFile prop:', this.initialFile);
    logger.log('🔍 AddTimeSheet enableDocumentUpload current value:', this.enableDocumentUpload);
    
    this.resetForm();
    
    // Ensure upload mode is set after resetForm if prop is true
    // Use $nextTick to ensure it's set after all watchers and resetForm complete
    this.$nextTick(() => {
      if (this.initialUploadMode) {
        this.enableDocumentUpload = true;
        logger.log('🔍 mounted: Set enableDocumentUpload to true after resetForm (initialUploadMode is true)');
      }
      
      // Set initial file if provided (after resetForm to ensure it's not cleared)
      if (this.initialFile && !this.extractionFile) {
        this.extractionFile = this.initialFile;
        // Create preview URL for the file
        if (!this.extractionFilePreviewUrl) {
          this.extractionFilePreviewUrl = URL.createObjectURL(this.initialFile);
        }
        logger.log('🔍 mounted: Set initial file after resetForm:', this.initialFile.name);
        
        // Automatically extract data from the initial file if not already extracted
        if (!this.hasExtractedData) {
          this.$nextTick(() => {
            this.extractTimesheetDataFromFile(this.initialFile, 0);
          });
        }
      }
      
      logger.log('🔍 mounted: Final enableDocumentUpload value:', this.enableDocumentUpload);
      logger.log('🔍 mounted: Final extractionFile:', this.extractionFile?.name || 'none');
    });
    
    this.fetchConsultants();

    // If we have prefilled consultant data, apply it after a short delay to ensure consultants are loaded
    if (this.prefilledConsultant && (this.prefilledConsultant.consultantName || this.prefilledConsultant.name)) {
      logger.log('🔍 Found prefilled consultant data, applying after delay');
      this.$nextTick(() => {
        setTimeout(() => {
          this.preFillConsultant(this.prefilledConsultant);
        }, 100);
      });
    }
  },
  beforeUnmount() {
    // Clean up all object URLs
    Object.values(this.attachmentPreviewUrls).forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    // Clean up uploaded file preview URL
    if (this.uploadedFilePreviewUrl) {
      URL.revokeObjectURL(this.uploadedFilePreviewUrl);
    }
    // Clean up resize event listeners
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
  }
};
</script>
